import { TcmsBaseClass } from './classes/TcmsBase.class.js';
import type { TcmsScrollNaviInterface, TcmsScrollNaviSettings } from './tcms_base_scrollNavi.types';

class TcmsScrollNavi extends TcmsBaseClass implements TcmsScrollNaviInterface {

    options!: TcmsScrollNaviSettings;

    sections: NodeListOf<HTMLElement> | null;

    navListHead: HTMLDivElement | null;

    navListContainer: HTMLDivElement | null;

    navListContainerInner: HTMLDivElement | null;

    currentActiveItemText: string;

    lazyLoaded: boolean;

    lastActiveElem: HTMLElement | null;

    initialized: boolean;

    containerElem: HTMLElement;

    constructor(element: HTMLElement, options?: Partial<TcmsScrollNaviSettings>) {
        super();

        this.options = {
            offsetTop: 300,
            scrollToOffsetToFromTop: 200,
            distance: 20,
            mobileBreakPoint: 1279,
            selector: '[data-scroll-navi-headline]:not([data-scroll-navi-headline=""])',
            navigationPrefix: 'navElem_',
            pageEndScrollOffset: false,
            hasTabs: false,
            navListHeader: {
                title: window.TCMS?.scrollNaviConfigs?.navListHeaderTitle || 'Contents'
            },
            className: {
                footer: 'c24-page-container-footer',
                open: 'open',
                fixed: 'fixed',
                show: 'show',
                active: 'active',
                navContainer: ['scroll-navigation', 'tcms_floating_sidebar'],
                navListHead: 'header',
                navListHeadLine: 'headline',
                navListHeadBurger: 'burgerIcon'
            },
            vertical: window.TCMS.product,
            dataTestId: {
                scrollNavi: 'tcms-widget-tam-scrollNavi',
                burger: 'tcms-widget-tam-scrollNavi-burger'
            }
        };

        this.options = this.mergeObject(this.options, options) as TcmsScrollNaviSettings;

        this.containerElem = element; // element on which the TcmsScrollNavi will be attached
        this.initialized = false;
        this.lazyLoaded = false;
        this.currentActiveItemText = this.options.navListHeader?.title;
        this.sections = null;
        this.navListHead = null;
        this.navListContainer = null;
        this.navListContainerInner = null;
        this.lastActiveElem = null;

        /* collect the sections */
        this.setSections();
    }

    handleNavListItemsClick(event: Event) {
        event.preventDefault();
        event.stopPropagation();

        const elem = event.target as HTMLAnchorElement;

        if (elem.parentElement?.className !== this.options.className.active) {
            const scrollTargetElement = document.getElementById(elem.hash.substr(1));

            if (!this.lazyLoaded) {
                window.fireAllVisible();
                window['c24_lazyloadLibs'].lazyLoad.loadAll();

                window.setTimeout(() => {
                    this.scrollSmooth(scrollTargetElement);
                    this.lazyLoaded = true;
                }, 500);
            } else {
                this.scrollSmooth(scrollTargetElement);
            }

            this.currentActiveItemText = elem?.innerText;
        }
    }

    toggleNavListHead() {
        if (!this.options.className) {
            return;
        }

        const classList = this.navListContainer?.classList;
        const openClass = this.options.className.open;
        const showClass = this.options.className.show;

        if (classList?.contains(showClass)) {
            classList?.remove(showClass);
        } else {
            classList?.add(showClass);
        }

        if (this.isMobile()) {
            if (classList?.contains(openClass)) {
                classList.remove(openClass);
                this.replaceHeadline(this.currentActiveItemText);
            } else if (!classList?.contains(openClass)) {
                classList?.add(openClass);
                this.replaceHeadline(this.options.navListHeader?.title || '');
            }
        }
    }

    resetNavListContainer() {
        if (this.isMobile()) {
            this.navListContainer?.classList.remove(this.options.className.open);
        }
    }

    isMobile() {
        // We cannot rely on "window.TCMS.isMobile" for mobile detection as it does not encompass tablets.
        const cw = document.documentElement.clientWidth || 0;
        const iw = window.innerWidth || 0;

        return Math.max(cw, iw) <= this.options.mobileBreakPoint;
    }

    initialize() {
        this.createNavContainer();

        const options = {
            root: null,
            rootMargin: '-1px 0px -99%',
            threshold: [0, 1]
        };

        const observer = new IntersectionObserver((entries) => this.handleIntersections(entries), options);

        observer.observe(this.containerElem);

        if (this.isMobile()) {
            const sectionOptions = {
                root: null,
                rootMargin: '-40px 0px -90%',
                threshold: [0, 1]
            };
            const sectionObserver = new IntersectionObserver(
                (entries) => this.handleIntersections(entries),
                sectionOptions
            );

            this.sections?.forEach((section) => {
                sectionObserver.observe(section);
            });
        } else {
            this.sections?.forEach((section) => {
                observer.observe(section);
            });
        }

        return true;
    }

    handleIntersections(entries: IntersectionObserverEntry[]): void {
        entries.forEach((entry) => {
            if (entry.target === this.containerElem) {
                this.handleContainerIntersection(entry);
            } else if (entry.isIntersecting) {
                this.setScrollNavElemActive(entry.target);
            }
        });
    }

    private handleContainerIntersection(entry: IntersectionObserverEntry): void {
        if (entry.isIntersecting && this.isMobile()) {
            this.navListContainer?.classList.add('fixed');
        } else {
            this.navListContainer?.classList.remove('fixed');
        }
    }

    createNavContainer() {
        if (!this.sections) {
            return false;
        }

        const navListContainer = document.createElement('div');

        navListContainer.classList.add(...this.options.className.navContainer);
        navListContainer.setAttribute('data-testid', this.options.dataTestId.scrollNavi);
        const navListInnerWrapper = document.createElement('div');

        navListInnerWrapper.classList.add('innerWrapper');

        this.navListHead = this.createNavContainerHead();

        navListContainer.appendChild(navListInnerWrapper);
        navListInnerWrapper.appendChild(this.navListHead);

        const navListItems = document.createElement('ul');

        this.sections.forEach((obj: HTMLElement) => {
            const navList = document.createElement('li');
            const navListElement = document.createElement('a');

            const handleClick = (event: Event) => {
                this.handleNavListItemsClick(event);
                this.toggleNavListHead();
            };

            navListElement.addEventListener('click', handleClick);

            if (obj.dataset.scrollnavBold === '1') {
                navListElement.classList.add('bold');
            }

            navList.id = obj.id + '_';

            navListElement.href = '#' + obj.id;
            navListElement.textContent = obj.dataset.scrollNaviHeadline || '';

            navList.appendChild(navListElement);
            navListItems.appendChild(navList);
        });

        navListInnerWrapper.appendChild(navListItems);

        if (this.containerElem) {
            this.containerElem.prepend(navListContainer);
        }

        this.navListContainerInner = navListInnerWrapper;
        this.navListContainer = navListContainer;
    }

    createNavContainerHead() {
        const navListHead = document.createElement('div');
        const navListHeadElement = document.createElement('span');
        const navListHeadBurgerWrapper = document.createElement('div');
        let navListHeadBurger = null;

        navListHeadElement.innerHTML = this.options.navListHeader?.title || '';
        navListHeadElement.classList.add(this.options.className.navListHeadLine);

        navListHead.classList.add(this.options.className.navListHead);
        navListHead.addEventListener('click', () => this.toggleNavListHead());

        for (let i = 0; i < 3; i++) {
            navListHeadBurger = document.createElement('span');
            navListHeadBurgerWrapper.appendChild(navListHeadBurger);
        }

        navListHeadBurgerWrapper.classList.add(this.options.className.navListHeadBurger);
        navListHeadBurgerWrapper.setAttribute('data-testid', this.options.dataTestId.burger);

        navListHead.appendChild(navListHeadBurgerWrapper);
        navListHead.appendChild(navListHeadElement);

        return navListHead;
    }

    setScrollNavElemActive(section: Element | null) {
        if (this.lastActiveElem) {
            this.lastActiveElem.removeAttribute('class');
        }

        if (section) {
            const navigationElement = document.getElementById(section.id + '_');

            if (navigationElement) {
                if (this.isMobile()) {
                    this.replaceHeadline(navigationElement.innerText);
                }
                navigationElement.classList.add(this.options.className.active);
                this.lastActiveElem = navigationElement;
            }
        }
    }

    scrollSmooth(scrollTargetElement: HTMLElement | null) {
        let offset = 0;

        if (this.isMobile() && typeof this.navListHead?.offsetHeight === 'number') {
            offset = this.navListHead.offsetHeight * -1;
        }

        window.scrollToTarget(scrollTargetElement, window, offset);
    }

    setSections() {
        const sections: NodeListOf<HTMLElement> = document.querySelectorAll(this.options.selector);

        if (sections.length > 0) {
            this.sections = sections;
        }
    }

    replaceHeadline(text: string) {
        const hasTextAndContainer = this.isMobile() && text && this.navListContainer;
        const navListHeadLineNode = this.navListContainer?.querySelector(
            `.${this.options.className.navListHeadLine}`
        ) as HTMLElement;

        if (hasTextAndContainer && navListHeadLineNode) {
            navListHeadLineNode.innerText = text;
        }
    }

}

if (window.TCMS.scrollNaviConfigs && typeof window.TCMS.scrollNaviConfigs.showScrollNavigation !== 'undefined') {
    const isApp =
        document.cookie.indexOf('deviceoutput=app') >= 0 || window.location.search.indexOf('deviceoutput=app') >= 0;

    if (!isApp) {
        import(`../styles/scrollNavi.scss`);
        const elem = document.getElementById('content') as HTMLElement;

        window.ck24_ScrollNavi = new TcmsScrollNavi(elem);

        if (NodeList.prototype.isPrototypeOf(window.ck24_ScrollNavi.sections)) {
            window.fireOnVisible(document.getElementById('content'), async() => {
                if (window.ck24_ScrollNavi.initialized === false) {
                    window.ck24_ScrollNavi.initialized = window.ck24_ScrollNavi.initialize();

                    if (
                        window.TCMS.hasOwnProperty('travelCta') &&
                        window.TCMS.travelCta.data.length > 0 &&
                        window.ck24_ScrollNavi.navListContainer
                    ) {
                        import('./classes/TcmsTravelCta.js').then(({ TcmsTravelCta }) => {
                            if (window.TCMS.travelCta.scrollNavi_instance === undefined) {
                                window.TCMS.travelCta.scrollNavi_instance = new TcmsTravelCta({
                                    travelCtaData: window.TCMS.travelCta.data,
                                    appendToNode: window.ck24_ScrollNavi.navListContainerInner,
                                    classes: ['travel_cta', 'scrollNavi'],
                                    useFixedPosition: false,
                                    eventTackingFunc: window.TCMS.trackingManager?.eventTracking
                                });
                            }
                        });
                    }
                }
            });
        } else {
            console.warn('NativScrollNavi: no scroll sections found!');
        }
    } else {
        console.info('NativScrollNavi: you have a cookie "deviceoutput=app" the scrollNavi is not activ on app!');
    }
}
